import { useRef, useState } from "react";
import { UploadCloud, ImageUp } from "lucide-react";

const COPY = {
  photo: {
    title: "Drop a portrait photo",
    body: "A clear, front-facing photo showing your outfit works best. We render you as a matte rubber-duck figurine, then sculpt it into a real printable 3D model.",
    ready: "Looks good — hit generate, or click to swap.",
    Icon: UploadCloud,
  },
  render: {
    title: "Drop your duck render",
    body: "Already have an AI image of your duck figurine? Upload it and we skip straight to the side views and the 3D sculpt.",
    ready: "Great — continue with this render, or click to swap.",
    Icon: ImageUp,
  },
};

// Left panel of step 1: drag & drop / click-to-pick image with a preview of the chosen file or sample.
export const UploadDropzone = ({ mode, previewUrl, onFile }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const { title, body, ready, Icon } = COPY[mode];

  return (
    <div
      data-testid="photo-dropzone"
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        onFile(e.dataTransfer.files?.[0]);
      }}
      onClick={() => inputRef.current?.click()}
      className={`relative cursor-pointer rounded-3xl border-2 border-dashed p-8 sm:p-12 min-h-[340px] flex flex-col items-center justify-center text-center transition-all ${
        dragging ? "border-accent bg-accent/5 scale-[1.01]" : "border-primary/40 hover:border-primary hover:bg-primary/5"
      }`}
    >
      <input ref={inputRef} data-testid="photo-dropzone-input" type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
      {previewUrl ? (
        <div className="flex flex-col items-center gap-4">
          <img src={previewUrl} alt="selected" className="h-44 w-44 object-cover rounded-2xl duck-shadow" />
          <p className="text-sm text-muted-foreground font-mono2">{ready}</p>
        </div>
      ) : (
        <>
          <div className="grid place-items-center h-20 w-20 rounded-2xl bg-primary/15 text-accent mb-5">
            <Icon className="h-10 w-10" />
          </div>
          <h3 className="font-heading text-xl sm:text-2xl font-bold">{title}</h3>
          <p className="text-muted-foreground mt-2 max-w-sm">{body}</p>
          <span className="mt-4 text-xs font-mono2 uppercase tracking-widest text-muted-foreground">PNG / JPG · click or drag & drop</span>
        </>
      )}
    </div>
  );
};
